import { NgModule } from '@angular/core';

import { AppRoutingModule } from './app-routing.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { DialogModule } from '@angular/cdk/dialog';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { CardSoftwareLibraryModule, SierraTheme, LOGIN_AUTH_SERVICE, LOGIN_CONFIG } from '@card-software/library';
import { AngularMaterialModule } from './angular-material.module';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { AppComponent } from './app.component';
import { LabDipRequestDetailsComponent } from './components/lab-dips/lab-dip-request-details/lab-dip-request-details.component';
import { LabDipRequestsComponent } from './components/lab-dips/lab-dip-requests/lab-dip-requests.component';
import { ProcessesComponent } from './components/processes/processes/processes.component';
import { ProcessDetailsComponent } from './components/processes/process-details/process-details.component';
import { StepsComponent } from './components/processes/steps/steps.component';
import { StepDetailsComponent } from './components/processes/step-details/step-details.component';
import { LabDipsComponent } from './components/lab-dips/lab-dips/lab-dips.component';
import { ParametersComponent } from './components/processes/parameters/parameters.component';
import { ParameterDetailsModalComponent } from './components/modals/parameter-details-modal/parameter-details-modal.component';
import { ParameterValuesModalComponent } from './components/modals/step-values-modal/parameter-values-modal.component';
import { ColorLabDipsManagementModalComponent } from './components/modals/color-lab-dips-management-modal/color-lab-dips-management-modal.component';
import { GreigesComponent } from './components/greige/greiges/greiges.component';
import { GreigeDetailsComponent } from './components/greige/greige-details/greige-details.component';
import { GeneralAdditionalParametersComponent } from './components/general-additional-parameters/general-additional-parameters.component';
import { FabricComponent } from './components/fabric/fabric/fabric.component';
import { FabricDetailsComponent } from './components/fabric/fabric-details/fabric-details.component';
import { YarnInfoComponent } from './components/shared/yarn-info/yarn-info.component';
import { authInterceptor } from './auth.interceptor';
import { siteInterceptor, errorInterceptor, SITE_API_BASE_URL } from '@card-software/nexus-utilities';
import { environment } from '../environments/environment';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { LabDipDetailsComponent } from './components/lab-dips/lab-dip-details/lab-dip-details.component';
import { CardColorComponent } from './components/card-color/card-color.component';
import { CardCompositionComponent } from './components/common/card-composition/card-composition.component';
import { CsNoticeComponent } from './components/common/cs-notice/cs-notice.component';
import { PrimengModule } from './PrimeNG/primeng.module';
import { ConfirmationService, MessageService } from 'primeng/api';
import { YarnComponent } from './components/yarn/yarn.component';
import { YarnDetailsModalComponent } from './components/modals/yarn-details-modal/yarn-details-modal.component';
import { CustomTranslateLoader } from './services/custom.translate.loader';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { FabricCategoriesComponent } from './components/fabric/fabric-categories/fabric-categories.component';
import { CategoriesModalComponent } from './components/modals/categories-modal/categories-modal.component';
import { FiberCategoriesComponent } from './components/fiber/fiber-categories/fiber-categories.component';
import { CategoriesTableComponent } from './components/common/categories-table/categories-table.component';
import { ItemCategoriesComponent } from './components/item/item-categories/item-categories.component';
import { ItemMasterComponent } from './components/item/item-master/item-master.component';
import { ItemMasterModalComponent } from './components/modals/item-master-modal/item-master-modal.component';
import { EditMasterdataDialogComponent } from './components/dialogs/edit-masterdata-dialog/edit-masterdata-dialog.component';
import { DialogService } from 'primeng/dynamicdialog';
import { TranslateWithFallbackPipe } from './pipes/translate-with-fallback.pipe';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { TranslateLocalizedPipe } from './pipes/translate-localized.pipe';
import { PascalToNormalCasePipe } from './pipes/pascal-to-normal-case.pipe';
import { CodesCreationsComponent } from './components/code-creation-request/codes-creations/codes-creations.component';
import { CodeCreationDetailComponent } from './components/code-creation-request/code-creation-detail/code-creation-detail.component';
import { LabDipDetailsModalComponent } from './components/modals/lab-dip-details-modal/lab-dip-details-modal.component';
import { RecipeDetailsComponent } from './components/dyeing/recipe-details/recipe-details.component';
import { RecipeContainerComponent } from './components/dyeing/recipe-container/recipe-container.component';
import { ProductRecipesComponent } from './components/product-recipe/product-recipes/product-recipes.component';
import { ProductRecipeDetailsComponent } from './components/product-recipe/product-recipe-details/product-recipe-details.component';
import { ItemDetailsViewerComponent } from './components/common/item-details-viewer/item-details-viewer.component';
import { StepDetailsViewerComponent } from './components/common/step-details-viewer/step-details-viewer.component';
import { ParameterDetailsViewerComponent } from './components/common/parameter-details-viewer/parameter-details-viewer.component';
import { ProcessDetailsViewerComponent } from './components/common/process-details-viewer/process-details-viewer.component';
import { ProcessDetailsTableComponent } from './components/common/process-details-table/process-details-table.component';
import { UserAuthenticationService } from './services/user-authentication.service';

@NgModule({
  declarations: [
    AppComponent,
    CardColorComponent,
    CardCompositionComponent,
    ColorLabDipsManagementModalComponent,
    ColorLabDipsManagementModalComponent,
    CsNoticeComponent,
    FabricComponent,
    FabricDetailsComponent,
    GeneralAdditionalParametersComponent,
    GreigeDetailsComponent,
    GreigeDetailsComponent,
    GreigesComponent,
    LabDipDetailsComponent,
    LabDipRequestDetailsComponent,
    LabDipRequestsComponent,
    LabDipsComponent,
    ParameterDetailsModalComponent,
    ParametersComponent,
    ParameterValuesModalComponent,
    ProcessDetailsComponent,
    ProcessesComponent,
    StepDetailsComponent,
    StepsComponent,
    YarnInfoComponent,
    YarnComponent,
    YarnDetailsModalComponent,
    FabricCategoriesComponent,
    CategoriesModalComponent,
    FiberCategoriesComponent,
    CategoriesTableComponent,
    ItemCategoriesComponent,
    ItemMasterComponent,
    ItemMasterModalComponent,
    EditMasterdataDialogComponent,
    UnauthorizedComponent,
    CodesCreationsComponent,
    CodeCreationDetailComponent,
    LabDipDetailsModalComponent,
    RecipeDetailsComponent,
    RecipeContainerComponent,
    ProductRecipesComponent,
    ProductRecipeDetailsComponent,
    ItemDetailsViewerComponent,
    StepDetailsViewerComponent,
    ParameterDetailsViewerComponent,
    ProcessDetailsViewerComponent,
    ProcessDetailsTableComponent,
  ],
  imports: [
    TranslateWithFallbackPipe,
    AngularMaterialModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    CardSoftwareLibraryModule,
    CommonModule,
    DialogModule,
    FormsModule,
    ReactiveFormsModule,
    PrimengModule,
    TranslateLocalizedPipe,
    PascalToNormalCasePipe,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslateLoader,
      },
    }),
  ],
  exports: [TranslateWithFallbackPipe, TranslateLocalizedPipe, PascalToNormalCasePipe],
  providers: [
    { provide: LOGIN_AUTH_SERVICE, useClass: UserAuthenticationService },
    {
      provide: LOGIN_CONFIG,
      useValue: {
        domainAppName: 'Sierra Item',
        contactEmail: 'it-support-sierra-textiles@card-software.com',
        redirectUrl: '/',
      },
    },
    provideHttpClient(withInterceptors([authInterceptor, siteInterceptor, errorInterceptor])),
    { provide: SITE_API_BASE_URL, useValue: environment.proxyUrls.userMasterApi },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: SierraTheme,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    ConfirmationService,
    MessageService,
    DialogService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
